import { useAppSelector } from 'store/hooks';
import { selectMerchantTimezone } from 'store/settings/settingsSlice';
import React from 'react';
import {
    Checkbox,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Switch,
    Typography
} from '@mui/material';
import getValue from 'get-value';
import { segmentsApi } from 'components/audience/segmentsApi';
import { CustomerSegment } from 'components/customers/models/Customer';
import { locationApi } from 'components/location/LocationApi';
import { Location } from 'components/location/models/LocationModel';
import { Column } from 'ui/Flex';
import { getPresetActionItemsOptions } from '../ContentModuleActions';
import {
    IDisplay,
    IItemContext,
    IMarketingAction,
    MarketingMessageItem,
    marketingTypeguards,
    MessageAction,
    MessageItem
} from '../models/ContentModule';
import { BrowserMessageProperties } from './marketing/BrowserMessagePropertiesForm';
import { NativeMessageProperties } from './marketing/NativeMessagePropertiesForm';
import { TableOrderProperties } from './marketing/TableOrderMessageProperties';
import { DefaultMarketingMessageProperties } from './marketing/DefaultMarketingMessageForm';
import { StartOrderMessageProperties } from './marketing/StartOrderMessagePropertiesForm';
import { LocationMessageProperties } from './marketing/LocationMessagePropertiesForm';
import { LocationsListMessageProperties } from './marketing/LocationsListMessagePropertiesForm';
import logger from 'lib/logger';
import { RowCopy } from 'lib/RowCopy';
import { ViewSubscriptionMessageProperties } from './marketing/ViewSubscriptionMessagePropertiesForm';
import { buildSchedule, ModuleSchedulingForm, toMerchantISO } from './module-scheduling/ModuleSchedulingForm';

const hasScheduling = (properties: any) =>
    !!(
        properties.visibleFrom ||
        properties.visibleTo ||
        (properties.schedule && properties.schedule.length > 0)
    );

interface MarketingModuleFormProps {
    display?: IDisplay;
    value?: MarketingMessageItem;
    onSubmit: (value: MarketingMessageItem) => void;
    close: () => void;
    formTitle: string;
}

interface PropertiesFormProps {
    action: IMarketingAction;
    message: MarketingMessageItem;
    handleSubmit: (properties: any) => void;
    contextForm: () => React.ReactNode;
    display: IDisplay;
    locations?: Location[];
}

const PropertiesForm = function PropertiesFormMemo({
    action,
    message,
    handleSubmit,
    contextForm,
    display,
    locations
}: PropertiesFormProps) {
    const newMessage = { ...message, action };

    if (marketingTypeguards.isBrowserMessage(newMessage)) {
        return (
            <BrowserMessageProperties
                name={newMessage.name}
                properties={newMessage.properties}
                context={newMessage.context}
                onSubmit={handleSubmit}
                contextForm={contextForm}
                display={display}
            />
        );
    }
    if (marketingTypeguards.isNativeMessage(newMessage)) {
        return (
            <NativeMessageProperties
                name={newMessage.name}
                properties={newMessage.properties}
                context={newMessage.context}
                onSubmit={handleSubmit}
                contextForm={contextForm}
                display={display}
            />
        );
    }
    if (marketingTypeguards.isLocationMessage(newMessage)) {
        return (
            <LocationMessageProperties
                name={newMessage.name}
                properties={newMessage.properties}
                context={newMessage.context}
                onSubmit={handleSubmit}
                contextForm={contextForm}
                display={display}
                locations={locations}
            />
        );
    }

    if (marketingTypeguards.isLocationsListMessage(newMessage)) {
        return (
            <LocationsListMessageProperties
                name={newMessage.name}
                properties={newMessage.properties}
                context={newMessage.context}
                onSubmit={handleSubmit}
                contextForm={contextForm}
                display={display}
                locations={locations}
            />
        );
    }

    if (marketingTypeguards.isStartOrderMessage(newMessage)) {
        return (
            <StartOrderMessageProperties
                name={newMessage.name}
                properties={newMessage.properties}
                context={newMessage.context}
                onSubmit={handleSubmit}
                contextForm={contextForm}
                display={display}
            />
        );
    }

    if (marketingTypeguards.isTableOrderMessage(newMessage)) {
        return (
            <TableOrderProperties
                name={newMessage.name}
                properties={newMessage.properties}
                context={newMessage.context}
                onSubmit={handleSubmit}
                display={display}
                contextForm={contextForm}
            />
        );
    }

    if (marketingTypeguards.isViewSubscriptionSchemeMessage(newMessage)) {
        return (
            <ViewSubscriptionMessageProperties
                name={newMessage.name}
                properties={newMessage.properties}
                context={newMessage.context}
                onSubmit={handleSubmit}
                display={display}
                contextForm={contextForm}
            />
        );
    }

    if (marketingTypeguards.isDefaultMarketingMessage(newMessage)) {
        return (
            <DefaultMarketingMessageProperties
                name={newMessage.name}
                properties={newMessage.properties}
                context={newMessage.context}
                onSubmit={handleSubmit}
                display={display}
                contextForm={contextForm}
            />
        );
    }

    if (!action) {
        return (
            <Grid size={12}>
                <Typography variant="caption">Select your marketing message type to start editing</Typography>
            </Grid>
        );
    }
    return (
        <Grid size={12}>
            <Typography>Sorry, this functionality isn&apos;t implemented yet</Typography>
        </Grid>
    );
};

const MarketingModuleForm = React.memo((props: MarketingModuleFormProps) => {
    const { close, onSubmit } = props;
    const { computedSettings } = useAppSelector(state => state.settings);
    const merchantTimezone = useAppSelector(selectMerchantTimezone);
    const [moduleType, setModuleType] = React.useState(props.value ? props.value.action : null);
    const [locationIds, setLocationIds] = React.useState(getValue(props, 'value.context.locationIds') || []);
    const [segmentIds, setSegmentIds] = React.useState(getValue(props, 'value.context.segmentIds') || []);
    const [authenticated, setAuthenticated] = React.useState(
        getValue(props, 'value.context.authenticated') ?? true
    );
    const [unauthenticated, setUnauthenticated] = React.useState(
        getValue(props, 'value.context.unauthenticated') ?? true
    );
    const [excludingSegmentIds, setExcludingSegmentIds] = React.useState(
        getValue(props, 'value.context.excludingSegmentIds') || []
    );
    const [locations, setLocations] = React.useState([] as Location[]);
    const [segments, setSegments] = React.useState<CustomerSegment[]>([]);
    const [addSchedule, setAddSchedule] = React.useState(
        props?.value?.context ? hasScheduling(props.value?.context) : false
    );

    React.useEffect(() => {
        async function getLocations() {
            try {
                setLocations(await locationApi.getAllLocations());
            } catch (e) {
                logger.error(e);
                setLocations([]);
            }
        }
        async function getSegments() {
            const result = await segmentsApi.getAll();
            if (!result) {
                return setSegments([]);
            }
            setSegments(result);
        }
        getLocations();
        getSegments();
    }, []);

    const locationsFieldHelperText = React.useMemo(() => {
        if (locations && locations.length <= 0) {
            return 'No locations available';
        }
        if (moduleType === MessageAction.LOCATIONS) {
            // eslint-disable-next-line max-len
            return 'Choose which locations you would like to display this content to when a user is checked in, or is set as the favourite location. Leaving this empty will show it to all locations.';
        }
        // eslint-disable-next-line max-len
        return 'Choose which locations you would like to display this content to when a user is checked in, or is set as the favourite location. Leaving this empty will show it to all locations.';
    }, [locations, moduleType]);

    const handleModuleChange = React.useCallback((e: SelectChangeEvent) => {
        setModuleType(e.target.value as IMarketingAction);
    }, []);
    const handleSubmit = React.useCallback(
        ({
            name,
            days,
            hasSpecificDates,
            startDateTime,
            endDateTime,
            schedule: _schedule,
            visibleFrom: _visibleFrom,
            visibleTo: _visibleTo,
            ...properties
        }: any) => {
            let schedulingValues = {};
            if (addSchedule) {
                const schedule = buildSchedule(days);
                schedulingValues = {
                    schedule: schedule.length ? schedule : undefined,
                    visibleFrom:
                        hasSpecificDates && startDateTime
                            ? toMerchantISO(startDateTime, merchantTimezone)
                            : undefined,
                    visibleTo:
                        hasSpecificDates && endDateTime
                            ? toMerchantISO(endDateTime, merchantTimezone)
                            : undefined
                };
            }
            const context: IItemContext = {
                authenticated,
                unauthenticated,
                ...(addSchedule && schedulingValues)
            };
            if (locationIds.length) {
                const activeLocationIds: Array<string> = [];
                for (const lid of locationIds) {
                    const found = locations.find(l => l._id === lid);
                    if (found) {
                        activeLocationIds.push(found._id);
                    }
                }
                if (activeLocationIds.length > 0) {
                    context.locationIds = activeLocationIds;
                }
            }
            if (segmentIds.length) {
                context.segmentIds = segmentIds;
            }
            if (excludingSegmentIds.length) {
                context.excludingSegmentIds = excludingSegmentIds;
            }
            onSubmit({
                id: props.value?.id,
                properties,
                context,
                action: moduleType,
                type: MessageItem.MESSAGE,
                index: 0,
                name: name?.length ? name : undefined
            });
            close();
        },
        [
            authenticated,
            unauthenticated,
            locationIds,
            segmentIds,
            excludingSegmentIds,
            onSubmit,
            props.value?.id,
            moduleType,
            locations,
            close,
            addSchedule,
            merchantTimezone
        ]
    );
    const handleSelectChange = React.useCallback((e: SelectChangeEvent) => {
        switch (e.target.name) {
            case 'segmentIds':
                setSegmentIds(e.target.value);
                return;
            case 'excludingSegmentIds':
                setExcludingSegmentIds(e.target.value);
                return;
            default:
                logger.warn('Unexpected select change event');
        }
    }, []);
    const handleCheckboxChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
            switch (e.target.name) {
                case 'authenticated':
                    setAuthenticated(checked);
                    return;
                case 'unauthenticated':
                    setUnauthenticated(checked);
                    return;
                default:
                    logger.warn('Unexpected checkbox change event');
            }
        },
        []
    );
    const handleLocationIdChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => setLocationIds(e.target.value),
        []
    );
    const toggleAddSchedule = React.useCallback(() => {
        setAddSchedule(prev => !prev);
    }, []);
    const contextForm = React.useCallback(
        () => (
            <React.Fragment>
                <Grid size={12}>
                    <Grid container direction="column">
                        <Typography color="primary">Context</Typography>
                        <Typography variant="caption">
                            Allow only certain groups to see this marketing module. All of these fields are
                            optional.
                        </Typography>
                    </Grid>
                </Grid>
                <Grid size={12}>
                    <InputLabel shrink>Locations</InputLabel>
                    <Select
                        multiple
                        value={locationIds}
                        fullWidth
                        onChange={handleLocationIdChange}
                        disabled={!locations || !locations.length}
                    >
                        {locations &&
                            locations.length &&
                            locations.map(location => (
                                <MenuItem key={location._id} value={location._id}>
                                    {location.title}
                                </MenuItem>
                            ))}
                    </Select>
                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1.4 }}>
                        {locationsFieldHelperText}
                    </Typography>
                </Grid>
                <Grid size={12}>
                    <InputLabel shrink>Show if user is in Audience</InputLabel>
                    <Select
                        multiple
                        value={segmentIds}
                        name="segmentIds"
                        fullWidth
                        onChange={handleSelectChange}
                        disabled={!segments || !segments.length}
                    >
                        {segments &&
                            segments.length &&
                            segments.map(segment => (
                                <MenuItem key={segment._id} value={segment._id}>
                                    {segment.title}
                                </MenuItem>
                            ))}
                    </Select>
                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1.4 }}>
                        {segments && segments.length
                            ? 'Choose which audiences you would like to display this content to. Leaving this empty will show it to all audiences.'
                            : 'No audiences available.'}
                    </Typography>
                </Grid>
                <Grid size={12}>
                    <InputLabel shrink>Hide if user is in Audience</InputLabel>
                    <Select
                        multiple
                        value={excludingSegmentIds}
                        name="excludingSegmentIds"
                        fullWidth
                        onChange={handleSelectChange}
                        disabled={!segments || !segments.length}
                    >
                        {segments &&
                            segments.length &&
                            segments.map(segment => (
                                <MenuItem key={segment._id} value={segment._id}>
                                    {segment.title}
                                </MenuItem>
                            ))}
                    </Select>
                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1.4 }}>
                        {segments && segments.length
                            ? // eslint-disable-next-line max-len
                              'Choose which audiences you would not like to display this content to. Leaving this empty will show it to all audiences.'
                            : 'No audiences available.'}
                    </Typography>
                </Grid>
                <Grid size={12}>
                    <Typography>Show content when user is:</Typography>
                </Grid>
                <Grid size={12}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                color="primary"
                                name="authenticated"
                                checked={authenticated}
                                onChange={handleCheckboxChange}
                            />
                        }
                        label="Signed In"
                    />
                </Grid>
                <Grid size={12}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                color="primary"
                                name="unauthenticated"
                                checked={unauthenticated}
                                onChange={handleCheckboxChange}
                            />
                        }
                        label="Signed Out"
                    />
                </Grid>
                <Grid size={12}>
                    <Paper variant="outlined" elevation={0} sx={{ p: 2 }}>
                        <FormControlLabel
                            control={<Switch checked={addSchedule} onChange={toggleAddSchedule} />}
                            label="Add schedule"
                        />
                        {addSchedule && <ModuleSchedulingForm showHeader={false} />}
                    </Paper>
                </Grid>
            </React.Fragment>
        ),
        [
            authenticated,
            excludingSegmentIds,
            handleCheckboxChange,
            handleLocationIdChange,
            handleSelectChange,
            locationIds,
            locations,
            segmentIds,
            segments,
            unauthenticated,
            locationsFieldHelperText,
            addSchedule,
            toggleAddSchedule
        ]
    );

    const presetOptions = React.useMemo(
        () =>
            getPresetActionItemsOptions({
                deliveryEnabled: computedSettings.deliveryEnabled
            }),
        [computedSettings.deliveryEnabled]
    );

    return (
        <Grid container spacing={3} sx={{ maxWidth: 512, p: 0 }}>
            <Grid size={12}>
                <Typography variant="h6" color="primary">
                    {props.formTitle}
                </Typography>
                {!!props.value?.id && <RowCopy value={props.value.id} text="ID" variant="subtitle2" />}
            </Grid>
            <Grid size={12}>
                <Select value={moduleType || ''} onChange={handleModuleChange} sx={{ width: '100%' }}>
                    {presetOptions.map(action => (
                        <MenuItem key={`select-${action.value}`} value={action.value}>
                            <Column>
                                {action.label}
                                <Typography variant="caption">{action.description}</Typography>
                            </Column>
                        </MenuItem>
                    ))}
                </Select>
            </Grid>
            <Grid size={12}>
                <PropertiesForm
                    action={moduleType}
                    message={props.value}
                    handleSubmit={handleSubmit}
                    contextForm={contextForm}
                    display={props.display}
                    locations={locations}
                />
            </Grid>
        </Grid>
    );
});

export const getMarketingModuleForm = (display?: IDisplay) => (props: MarketingModuleFormProps) =>
    <MarketingModuleForm display={display} {...props} />;
